import axios from "axios";
import { useState, useEffect, useCallback } from "react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

export const fmtMoney = (n) => "$" + Number(n || 0).toLocaleString();
export const fmtMoneyShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000000) return "$" + (v / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  if (Math.abs(v) >= 1000) return "$" + (v / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  return "$" + v.toLocaleString();
};

export const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export const fmtDateTime = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " · " +
    d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

export const fmtTime = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

export const timeAgo = (v) => {
  if (!v) return "";
  const d = new Date(v);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return Math.floor(s / 60) + "m ago";
  if (s < 86400) return Math.floor(s / 3600) + "h ago";
  return Math.floor(s / 86400) + "d ago";
};

export const CAPTURE_CATEGORIES = [
  "Lead", "Customer", "Payment", "Person", "Task", "Meeting", "Project Update",
  "Opportunity", "Problem", "Idea", "Intelligence", "Relationship", "Social Idea", "Important Information",
];

export const LEAD_STAGES = [
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "follow_up", label: "Follow-Up" },
  { key: "hot", label: "Hot" },
  { key: "proposal", label: "Proposal" },
  { key: "payment_pending", label: "Payment Pending" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

export const MONEY_STATUSES = [
  "lead", "proposal", "promised", "payment_pending", "partial", "paid", "overdue", "disputed", "lost",
];

export const TASK_STATUSES = [
  { key: "todo", label: "To Do" },
  { key: "in_progress", label: "In Progress" },
  { key: "waiting", label: "Waiting" },
  { key: "done", label: "Done" },
];

export const PROJECT_STATUS = {
  green: { label: "Green", color: "#10B981" },
  yellow: { label: "Yellow", color: "#F59E0B" },
  red: { label: "Red", color: "#E11D48" },
  paused: { label: "Paused", color: "#A3A3A3" },
  completed: { label: "Completed", color: "#3B82F6" },
};

export const RELATIONSHIP_STRENGTH = {
  1: "New", 2: "Know", 3: "Relationship", 4: "Strong", 5: "Inner Circle",
};

export function moneyStatusColor(s) {
  if (["paid"].includes(s)) return "#10B981";
  if (["overdue", "disputed", "lost"].includes(s)) return "#E11D48";
  if (["promised", "payment_pending", "partial"].includes(s)) return "#F59E0B";
  return "#A3A3A3";
}

export function leadStageColor(s) {
  if (s === "won") return "#10B981";
  if (s === "lost") return "#A3A3A3";
  if (["hot", "payment_pending"].includes(s)) return "#E11D48";
  if (["proposal", "follow_up"].includes(s)) return "#F59E0B";
  return "#D4AF37";
}

export function useCollection(name) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/${name}`);
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [name]);
  useEffect(() => { load(); }, [load]);
  const create = async (d) => { const { data } = await api.post(`/${name}`, d); await load(); return data; };
  const update = async (id, d) => { const { data } = await api.put(`/${name}/${id}`, d); await load(); return data; };
  const remove = async (id) => { await api.delete(`/${name}/${id}`); await load(); };
  return { items, loading, load, create, update, remove, setItems };
}
